import { type FC } from 'react'

import {
  Box,
  Button,
  Icon,
  Table,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import UserAvatar from 'modules/core/components/UserAvatar'
import createComputed from 'modules/core/hooks/createComputed'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import users from 'modules/core/store/collections/users'

import { employeeReferenceContract } from 'modules/job_catalog/lib/contractVersions'
import contractVersions from 'modules/contracts/store/collections/contractVersions'
import type Employee from 'modules/employees/store/models/Employee'
import { formatSalaryFromCents, isSalaryOnRange } from 'modules/job_catalog/lib/salaryUtils'
import salaryRanges from 'modules/job_catalog/store/collections/salaryRanges'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

const getEmployeeUser = (employee: Employee) => {
  const userId = employee.attributes.access_id
  const user = users.get(userId)
  if (!user) {
    throw Error(`Error getting users with id = ${userId}`)
  }
  return user
}

interface Props {
  employee: Employee
  level: { id: string; name: string }
  showSalaryAmountColumn: boolean
}

const useMemoHiredOn = createComputed(
  (employee) => employeeReferenceContract(contractVersions, employee)?.startsOn?.fromNow() || '-'
)

const Row: FC<Props> = ({ employee, level, showSalaryAmountColumn }) => {
  const [open, toggle] = useToggle(false)
  const { navigation } = useNavigation()
  const hiredOn = useMemoHiredOn(employee)

  const legalEntity = employeeLegalEntity(employee)
  const legalEntityCountry = legalEntity.country
  const canSeeGrossSalary = policyAuthorizations.for('contracts.see_gross_salary', employee)
  const legalEntityId = legalEntity.attributes.id
  const salaryFromLevel = level && salaryRanges.fromJobLevelLegalEntity(legalEntityId, level?.id)
  const legalEntityCurrency = legalEntity.attributes.currency

  const { min_range, max_range, salary_frequency, amount_type, currency } =
    (salaryFromLevel && salaryFromLevel.attributes) || {}

  const contractAmount = employeeReferenceContract(contractVersions, employee)?.attributes
    .salary_amount
  const contractFrequency = employeeReferenceContract(contractVersions, employee)?.attributes
    .salary_frequency
  const formattedAmount = formatSalaryFromCents(
    contractAmount ?? undefined,
    legalEntityCountry,
    currency || legalEntityCurrency
  )

  const onRange = isSalaryOnRange(
    contractAmount,
    contractFrequency,
    salary_frequency,
    min_range,
    max_range,
    amount_type
  )
  const getSalaryFrequency = (salaryFrequency: string | undefined) => {
    switch (salaryFrequency) {
      case 'yearly':
        return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.yearly')
      case 'monthly':
        return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.monthly')
      case 'weekly':
        return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.weekly')
      case 'daily':
        return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.daily')
      case 'hourly':
        return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.hourly')
    }
  }

  const outOfRangeMessage =
    amount_type === 'range'
      ? i18n.t('contract_versions.forms.gross_salary.factorial_tip.out_of_range_message', {
          salary_frequency: getSalaryFrequency(salary_frequency),
          min_range: formatSalaryFromCents(min_range, legalEntityCountry, currency),
          max_range: formatSalaryFromCents(max_range, legalEntityCountry, currency),
          currency,
        })
      : i18n.t('contract_versions.forms.gross_salary.factorial_tip.out_of_range_fixed_message', {
          salary_frequency: getSalaryFrequency(salary_frequency),
          max_range: formatSalaryFromCents(max_range, legalEntityCountry, currency),
          currency,
        })

  return (
    <Table.Row>
      <Table.Cell colSpan={4}>
        <Box flexDirection="row" alignItems="center" gap="s16">
          <UserAvatar size="s32" user={getEmployeeUser(employee)} />
          <Text.Normal>{getEmployeeUser(employee).attributes.first_name?.trim() || ''}</Text.Normal>
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={4}>
        <Box flexDirection="row" alignItems="center">
          <Text.Normal>{getEmployeeUser(employee).attributes.last_name?.trim() || ''}</Text.Normal>
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={4}>
        <Box flexDirection="row" alignItems="center">
          <Text.Normal>{hiredOn.get()}</Text.Normal>
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={4}>
        <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween" gap="s24">
          <Text.Normal>
            {employee.manager &&
              getFullDisplayName({
                firstName: getEmployeeUser(employee.manager).attributes.first_name,
                lastName: getEmployeeUser(employee.manager).attributes.last_name,
                canSharePreferredName: employee.manager.policy.can('sharePreferredName'),
                preferredName: employee.manager.attributes.preferred_name,
              })}
          </Text.Normal>
        </Box>
      </Table.Cell>
      {showSalaryAmountColumn && (
        <Table.Cell colSpan={4}>
          <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween" gap="s24">
            {canSeeGrossSalary && contractAmount && contractAmount > 0 ? (
              <Text.Normal>{formattedAmount}</Text.Normal>
            ) : (
              <Text.Normal>{i18n.t('common.not_applicable')}</Text.Normal>
            )}
            {canSeeGrossSalary && min_range && max_range && !onRange && (
              <Tooltip.Alt
                attachment="bottom center"
                open={open}
                toggle={toggle}
                label={outOfRangeMessage}
              >
                <Icon.Small icon={'alert'} color="grey000White" encircled={'radicalRed800'} />
              </Tooltip.Alt>
            )}
          </Box>
        </Table.Cell>
      )}
      <Table.Cell colSpan={2}>
        <Box flexDirection="row" justifyContent="flexEnd">
          <Button.Router
            type="secondary"
            to={navigation.employees.id.fullPath({ id: employee.attributes.id.toString() })}
            iconName="arrow-right"
          />
        </Box>
      </Table.Cell>
    </Table.Row>
  )
}

export default observer(Row)
